import { timing, variantForLevel } from './params';
const FEEDBACK_MS = timing.feedbackMs;
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
function manhattan(a, b) {
    return Math.abs(a.row - b.row) + Math.abs(a.col - b.col);
}
function adjacent(a, b) {
    return manhattan(a, b) === 1;
}
export function buildPuzzle(trialIdx, nCheckpoints, level) {
    const variant = variantForLevel(level);
    const N = variant.gridSize;
    const start = { row: 0, col: 0 };
    const goal = { row: N - 1, col: N - 1 };
    const safeK = Math.max(timing.minCheckpoints, Math.min(nCheckpoints, N * N - 4));
    // Ensemble interdit : start, goal, et déjà choisis
    const reserved = new Set([`${start.row}-${start.col}`, `${goal.row}-${goal.col}`]);
    const checkpoints = [];
    let attempts = 0;
    while (checkpoints.length < safeK && attempts < 200) {
        attempts += 1;
        const c = {
            row: Math.floor(Math.random() * N),
            col: Math.floor(Math.random() * N),
        };
        const k = `${c.row}-${c.col}`;
        if (reserved.has(k))
            continue;
        reserved.add(k);
        checkpoints.push(c);
    }
    // Optimal path length = sum of manhattan distances (start → cp1 → cp2 → ... → goal).
    let optimal = 0;
    let prev = start;
    for (const cp of checkpoints) {
        optimal += manhattan(prev, cp);
        prev = cp;
    }
    optimal += manhattan(prev, goal);
    return {
        trialIdx,
        gridSize: N,
        start,
        goal,
        checkpoints,
        optimalLength: optimal,
        optimalityTolerance: variant.optimalityTolerance,
    };
}
export function createPathFinderAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        puzzle: null,
        taps: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function evaluate(taps, puzzle) {
        if (taps.length === 0)
            return { ok: false };
        const last = taps[taps.length - 1];
        if (!cellEq(last, puzzle.goal))
            return { ok: false };
        // Vérifier que tous les checkpoints sont dans la séquence dans l'ordre.
        let cpIdx = 0;
        for (const t of taps) {
            if (cpIdx < puzzle.checkpoints.length && cellEq(t, puzzle.checkpoints[cpIdx])) {
                cpIdx += 1;
            }
        }
        if (cpIdx < puzzle.checkpoints.length)
            return { ok: false };
        // Longueur tolérance : taps comprend start (1 tap) + 1 par déplacement.
        const moves = taps.length - 1; // start est implicit ou explicite ?
        // Note : on compte le path comme séquence de cellules visitées (sans start auto-inclus
        // si user tap explicitement start en premier). Pour MVP : on ne pré-tap pas start.
        if (moves > puzzle.optimalLength * puzzle.optimalityTolerance)
            return { ok: false };
        return { ok: true };
    }
    function settle(correct, finalTaps) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ taps: finalTaps, correct, reactionTimeMs: rt, reasonOk: correct });
    }
    return {
        mount() { },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(puzzle) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'pre_puzzle', puzzle, taps: [puzzle.start], feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'awaiting_input' });
                    trialStart = Date.now();
                }, timing.prePuzzleMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleTap(cell) {
            if (state.subPhase !== 'awaiting_input' || !state.puzzle)
                return;
            const last = state.taps[state.taps.length - 1];
            if (!last)
                return;
            // Tap doit être adjacent au précédent
            if (!adjacent(last, cell))
                return;
            // Pas de revisite trivial (le tap juste avant)
            if (state.taps.length >= 2 && cellEq(state.taps[state.taps.length - 2], cell)) {
                // Backtrack : on retire le dernier tap
                const newTaps = state.taps.slice(0, -1);
                update({ taps: newTaps });
                return;
            }
            const newTaps = [...state.taps, cell];
            update({ taps: newTaps });
            // Si le user atteint le goal, on évalue.
            if (cellEq(cell, state.puzzle.goal)) {
                const { ok } = evaluate(newTaps, state.puzzle);
                // Show feedback via showFeedback callback (runtime appelle après grader).
                // Ici on settle le promise.
                settle(ok, newTaps);
            }
        },
        resetPath() {
            if (state.subPhase !== 'awaiting_input' || !state.puzzle)
                return;
            update({ taps: [state.puzzle.start] });
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map